import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
// eslint-disable-next-line import/no-extraneous-dependencies
import qs from 'qs';

import { AxiosServiceResponse, PagedList } from 'interfaces/general';

const instance = axios.create({
  baseURL: `${process.env.SECURITY_SERVICE_ENDPOINT}/api/v1`,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest'
  }
});

export interface SearchParams {
  Id?: number;
  Email?: string;
  FirstName?: string;
  FirstLastName?: string;
  SecondLastName?: string;
  ClientId?: number;
  RoleId?: number;
  enabled?: boolean;
  TimeZoneId?: number;
  OrderColumn?: string;
  Descending?: boolean;
  Text?: string;
  isExport?: boolean;
  partnerId?: number[];
}

export interface GeneralSearch {
  pageNumber?: number;
  pageSize?: number;
  orderColumn?: string;
  descending?: boolean;
  text?: string;
  isExport?: boolean;
  engineerOnly?: boolean;
  enabled?: boolean;
  clientId?: number | string;
}
export interface AccessList {
  accessElementId: number;
  accessKey: string;
  description: string;
}

export interface GetUserDetail {
  userId?: number;
  email?: string;
  firstName?: string;
  firstLastname?: string;
  secondLastName?: string;
  fullName?: string;
  gender?: string;
  systemRoleId?: number;
  systemRoleName?: string;
  enabled?: boolean;
  phoneNumber?: string;
  userName?: string;
  blobStorageUrlPath?: string;
  userResponsableId?: number;
  partnersId?: number[];
  partnersName?: string;
  responsibleId?: number;
  langId: number;
  accessTypeId?: number;
}

export interface UserItemByID {
  userId: number;
  email: string;
  firstName: string;
  firstLastname: string;
  secondLastName: string;
  fullName: string;
  systemRoleId: number;
  systemRoleName: string;
  enabled: boolean;
  phoneNumber: string;
  userName: string;
  areaId: number;
  areaIds?: number[];
  areasId?: number[]; // Backend returns this property name
  areas?: { id: number; name: string }[]; // Backend also returns full area objects
  areaName: string;
  acceptedTerms: boolean;
  userResponsableId: number;
  partnersId: number[];
  partnersName: string;
  responsibleId: number;
  nonOperational: boolean;
  managerId: number;
  managerName: string;
  regionId: number;
  regionName: string;
  hierarchicalLevelId: number;
  hierarchicalLevelName: string;
  systemGroups: { id: number; name: string }[];
  provider: boolean;
  contractFileDescriptorId: number;
  contractFileDescriptor: string;
  contractNumber: string;
  businessName?: string;
  razonSocial?: string;
  employeeNumber?: number | null;
}

export interface UserItem {
  id: number;
  userName: string;
  email: string;
  firstName: string;
  firstLastName: string;
  secondLastName: string;
  gender: string;
  systemRoleId: number;
  systemRoleName: string;
  timeZoneId: number;
  timeZoneIdName: string;
  enabled: boolean;
  deleted: boolean;
  fullName: string;
  credential?: string;
  partnerName?: string;
  responsibleId: number;
  itemsCustom?: string[];
  dynamicFormFields?: { formFieldId: string | number; value: string }[];
  dynamicFormFieldsKeyVault?: { [key: string]: string };
  firstLastname?: string;
  areaName?: string;
  areas?: { id: number; name: string }[]; // Múltiples áreas con id y nombre
  nonOperational?: boolean;
  provider?: boolean;
  businessName?: string;
  razonSocial?: string;
  employeeNumber?: number | null;
}

export interface RoleResponse {
  id: number;
  name: string;
  description: string;
}
export interface Access {
  accessElementId: number;
  accessKey?: string;
  description: string;
  isAsociated?: boolean;
}

export interface AccessUser {
  accessElementId: number;
  description: string;
  checked: boolean;
}
export interface AccessElements {
  module: string;
  access: Access[];
  accessList?: number[];
}

export interface AccessElementsUser {
  module: string;
  accessList: AccessUser[];
}

export interface PermitionsResponse {
  roleId: number;
  roleName: string;
  roleDescription: string;
  accessElements: Access[];
}

export interface CreateNewUseRequest {
  email?: string;
  userName?: string;
  firstName?: string;
  firstLastname?: string;
  secondLastName?: string;
  gender?: string;
  systemRoleId?: number;
  timeZoneId?: number;
  phoneNumber?: string;
  credential?: string;
  areaId?: number;
  AreaIds?: number[];
  AreasId?: number[]; // Siempre usar AreasId, incluso para una sola área
  areaManagerId?: number;
  partnersId?: number[];
  systemGroupsId?: number[];
  accountReactivation?: boolean;
  provider?: boolean;
  contractFileDescriptor?: string;
  responsibleId?: number;
  contractFileDescriptorId?: number;
  contractNumber?: string;
  businessName?: string;
  razonSocial?: string;
  nonOperational?: boolean;
  regionId?: number;
  managerId?: number;
  hierarchicalLevelId?: number;
  employeeNumber?: number | null;
}

export interface EditUseRequest {
  id: number;
  email: string;
  userName: string;
  firstName: string;
  firstLastname: string;
  secondLastName: string;
  gender?: string;
  systemRoleId: number;
  timeZoneId?: number;
  phoneNumber: string;
  credential: string;
  AreasId?: number[]; // Siempre usar AreasId, incluso para una sola área
  AreaIds?: number[];
  areaManagerId: number;
  enabled?: boolean;
  partnersId?: number[];
  systemGroupsId: number[];
  itemsCustom?: string[];
  nonOperational?: boolean;
  regionId?: number;
  managerId?: number;
  hierarchicalLevelId?: number;
  provider?: boolean;
  contractFileDescriptor?: string;
  contractFileDescriptorId?: number;
  contractNumber?: string;
  businessName?: string;
  razonSocial?: string;
  employeeNumber?: number | null;
}

export interface AccessElementProfile {
  elementId: number;
}
export interface ProfileRequest {
  roleName: string;
  roleDescription: null;
  enabled: boolean;
  accessElements: AccessElementProfile[];
}
export interface ProfileEditionRequest {
  roleId: number;
  roleName: string;
  roleDescription: null;
  enabled: boolean;
  accessElements: Access[];
}

export interface UpdateAccountEmail {
  email?: string;
  newEmail?: string;
}

export interface UpdateProfileRequest {
  userId: number;
  accessList: number[];
}

export interface AccessLogbookParams {
  FromDate?: Date | null;
  ToDate?: Date | null;
  TokenType?: string;
  AreaId?: number | null;
  PageNumber?: number | null;
  PageSize?: number | null;
  OrderColumn?: string;
  Descending?: boolean;
  Text?: string;
  Username?: string;
  FullName?: string;
  IsExport?: boolean;
}

export interface CloneUserAccessElement {
  sourceUserId: number;
  destinationUsersId: number[];
}

export interface AccessLogbookResponse {
  id: number;
  username: string;
  fullName: string;
  email: string;
  accessDate: Date;
  lastAccessDate: Date;
  tokenType: string;
  apkversion: string;
  imei: string;
  daysSinceLastAccess: number;
  IPaddress: string;
}

export interface TokenType {
  id: number;
  name: string;
}

export interface WorkFlow {
  workFlowId: number;
  workFlowName: string;
  checked: boolean;
}

export interface WorkFlowType {
  workFlowTypeId: number;
  workFlowTypeName: string;
  checked: boolean;
  workFlows?: WorkFlow[];
}
export interface OperatingPermissionClient {
  checked: boolean;
  clientName: string;
  workFlowTypes?: WorkFlowType[];
}

export interface OperatingPermission {
  clients: OperatingPermissionClient[];
}

export interface UserWorkFlowTypeList {
  workFlowTypeId: number;
  workFlowTypeName: string;
  checked: boolean;
}
export interface SubmitOperatingPermission {
  userToAssignId: number;
  partnerId: number;
  userWorkFlowList: WorkFlow[];
}

export interface Assignation {
  assignation: string;
  checked: boolean;
}

export interface CloneUserAssignations {
  sourceUserId: number;
  destinationUsersId: number[];
  assignations: Assignation[];
  replaceAssignations: boolean;
}

export interface WarehouseAdminUser {
  systemUserId: number;
  responsibleId: number;
  name: string;
}

export interface ClientsAsociatedToUserresponse {
  clientId: number;
  clientName: string;
  checked: boolean;
}

export interface RoleItem {
  id: number;
  description: string;
  enabled: true;
  name: string;
}

export interface WarehouseItem {
  warehouseId: number;
  warehouseName: string;
  checked: boolean;
}

export interface WorkForceItem {
  workForceId: number;
  workForceName: string;
  checked: boolean;
}

export interface CommentTypeItem {
  ticketCommentTypeId: number;
  ticketCommentTypeName: string;
  checked: boolean;
}

export interface AccessTypeItem {
  accessTypeId: number;
  accessTypeName: string;
  checked: boolean;
}

export interface GeneralPermissionsResponse {
  accessTypeList?: AccessTypeItem[];
}

export interface Service {
  id: number;
  name: string;
  checked: boolean;
}

export interface ServiceType {
  serviceTypeName: string;
  services: Service[];
}

export interface ClientService {
  clientName: string;
  servicesTypes: ServiceType[];
}

export interface UserServiceList {
  clients: ClientService[];
}

export interface SettingsUserPayload {
  userToAssignId: number;
  userServiceList: {
    serviceId: number;
    serviceName: string;
    checked: boolean;
  }[];
}
export interface GenerateDefaultUserName {
  firstName: string;
  firstLastName: string;
}

export interface AddLogingTrackingRequest {
  userLoggedId: number;
  username?: string;
  fullName?: string;
  tokenTypeId: number;
  apkversion?: string;
  imei?: string;
  isLogin: boolean;
}

export interface AssignUserWarehouseRequest {
  userToAssignId: number;
  partnerId: number;
  userWarehouseList: {
    warehouseId: number;
    warehouseName: string;
    checked: boolean;
  }[];
}

export interface AssignUserClientsRequest {
  userToAssignId: number;
  partnerId: number;
  userClientList: {
    clientId: number;
    clientName: string;
    checked: boolean;
  }[];
}

export interface AssignUserWorkForcesRequest {
  userToAssignId: number;
  partnerId: number;
  userWorkforceList: {
    workforceId: number;
    workforceName: string;
    checked: boolean;
  }[];
}

export interface DataClientsByUser {
  clients?: ClientsByUser[];
}

export interface ClientsByUser {
  clientId?: number;
  clientName?: string;
  clientDescription?: string;
  projectName?: string;
  services?: ServiceRelatedToClientsByUser[];
}

export interface ServiceRelatedToClientsByUser {
  serviceName?: string;
  serviceTypeName?: string;
}

export interface WorkForceRelatedToUserIdParams {
  userId: number;
  enabled?: boolean;
  pageNumber?: number;
  orderColumn?: string;
  descending?: boolean;
  text?: string;
  isExport?: boolean;
  pageSize?: number;
}

export interface WorkForceRelatedToUserIdResponse {
  workForceId?: number;
  workForceName?: string;
  userName?: string;
  areaManager?: string;
  area?: string;
  enabled?: boolean;
}

export interface CanWarehouseAdminBeInactivate {
  canProceed?: boolean;
  canInactivateWarehouses?: CanInactivateWarehouse[];
  warehouseAdminWhitWarehouse?: string[];
}

export interface CanInactivateWarehouse {
  warehouseName?: string;
  adminName?: string;
  stillHasSecondaryAdmins?: boolean;
}

export interface GetParamsResponse {
  Server: string;
  Version: string;
  About: string;
  Phones: {
    Data: string;
  };
  Questions: {
    Data: string;
  };
  GoogleChat: {
    Link: string;
  };
  BI: { LocationResume: string };
}

export interface ISystemGroupAccessElementList {
  accessElementId: number;
  description: string;
  checked: boolean;
}

export interface AssignSystemGroupAccessElements {
  systemGroupToAssignId: number;
  systemGroupAccessElementList: ISystemGroupAccessElementList[];
}

export interface SystemGroup {
  id: number;
  name: string;
  description: string;
  enabled: boolean;
  engineerOnly?: boolean;
  systemGroupUsers?: GetUserDetail[];
  groupUsers?: GetUserDetail[];
  systemUsersIds?: number[];
  clientsIds?: number[];
}

export interface SystemGroupUsersSearch {
  pageNumber: number;
  pageSize: number;
  orderColumn: string;
  descending: boolean;
  text: string;
  isExport: boolean;
  id: number;
  systemGroupId: number;
}

export interface GeneralParams {
  Login?: Login;
  Export?: Export;
  Batch?: Batch;
  GoogleChat?: GoogleChat;
  KPIRegex?: KPIRegex;
  BI?: BI;
  Wiki?: Wiki;
}

export interface BI {
  LocationResume?: string;
}

export interface Batch {
  ExtendedTimeOut?: string;
}

export interface Export {
  MaxExportLimit?: string;
}

export interface GoogleChat {
  Link?: string;
}

export interface KPIRegex {
  Formula?: string;
  OpenedBrackets?: string;
  ClosedBrackets?: string;
}

export interface Login {
  DaysToCheckLoginCount?: string;
  MinAccessCount?: string;
}

export interface Wiki {
  Url?: string;
}

export interface WorkForceRelatedToClientIdParams {
  ClientId?: number;
  PartialName?: string;
}

export interface WorkForceRelatedToClientIdResponse {
  id?: number;
  responseId?: number;
  name?: string;
  firstLastName?: string;
  secondLastName?: string;
  fullName?: string;
  email?: string;
}

export interface ISystemGroupClientListParams {
  groupId: number;
  partnerId: number;
  text?: string;
}

export enum PermissionType {
  OperationPermission = 'OperationPermission',
  AdditionalUserPermission = 'AdditionalUserPermission'
}

export interface IAccessElementTrackingParams extends GeneralSearch {
  systemUserId?: number;
  permissionType?: PermissionType;
  groupName?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface IAccessElementTracking {
  id: number;
  accessElementName: string;
  accessElementGroupName: string;
  permissionType?: PermissionType;
  actionType?: string;
  systemUserId: number;
  systemUserName: string;
  active: boolean;
  createdUserId: number;
  createdUserName: string;
  createdDateTime: Date | string;
  enabled: boolean;
}

export interface OrganizationalStructureParams {
  RegionId?: number;
  AreaId?: number;
  HierarchicalLevelId?: number;
  SearchPerson?: string;
  RootUserId?: number;
  IncludeVacantPositions?: boolean;
  MaxDepth?: number;
  PageNumber?: number;
  OrderColumn?: string;
  Descending?: boolean;
  Text?: string;
  IsExport?: boolean;
  PageSize?: number;
  ReportType?: string;
  offset?: number;
  systemUserIds?: number[];
}

export interface ValidationInfo {
  isValid: boolean;
  validationErrors: string[];
  validationWarnings: string[];
  regionsWithoutDirector: Array<{
    id: number;
    name: string;
  }>;
}

export interface HierarchicalStructureItem {
  id: number;
  levels: Levels;
  endUser: EndUser;
  manager: Manager;
}

export interface Levels {
  id: number;
  fullName: string;
  userName: string;
  email: string;
  enabled: boolean;
  areaName: string;
  areaNames?: string[]; // Múltiples áreas concatenadas
  regionName: string;
  hierarchicalLevelName: string;
  user: string;
  area: string;
  areas?: string[]; // Múltiples áreas en formato array
  region: string;
}

export interface EndUser {
  id: number;
  fullName: string;
  userName: string;
  email: string;
  enabled: boolean;
  areaName: string;
  areaNames?: string[]; // Múltiples áreas concatenadas
  regionName: string;
  hierarchicalLevelName: string;
}

export interface Manager {
  id: number;
  fullName: string;
  userName: string;
  email: string;
  enabled: boolean;
  areaName: string;
  areaNames?: string[]; // Múltiples áreas concatenadas
  regionName: string;
  hierarchicalLevelName: string;
}

export interface OrganizationalStructureData {
  validationInfo: ValidationInfo;
  totalUsers: number;
  totalVacantPositions: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  totalCount: number;
  hasPrevious: boolean;
  hasNext: boolean;
  items: HierarchicalStructureItem[];
}

export interface OrganizationalStructureResponse {
  data: OrganizationalStructureData;
  message: string;
  isError: boolean;
}

export interface GetManagerCandidatesParams {
  Text?: string;
  AreaId?: number;
  RegionId?: number;
  HierarchicalLevelId?: number;
  ExcludeUserId?: number;
}

export interface ManagerCandidateResponse {
  id: number;
  userName: string;
  fullName: string;
  hierarchicalLevelName: string;
  displayText: string;
  areaId: number;
  regionId: number;
  hierarchicalLevelId: number;
}

export interface OpenTicketsCountResponse {
  openTicketCount: number;
  ticketList: number[];
}

export interface PeopleInChargeItem {
  userFullName: string;
  levelName: string;
  areaName: string;
  regionName: string;
  countryName: string;
  enabled: boolean;
}

export interface PeopleInChargeResponse {
  data: PeopleInChargeItem[];
  message: string;
  isError: boolean;
}

export interface HierarchicalMassiveLoad {
  hierarchyUpdates: [
    {
      index: number;
      userToReassignId: number;
      userToReassignName: string;
      newManagerId: number;
      newManagerName: string;
    }
  ];
  withoutSaving: boolean;
}

export interface BulkLoadHierarchicalStructureResponse {
  hierarchyUpdatesProcessed: {
    index: number;
    userToReassignId: number;
    userToReassignName: string;
    newManagerId: number;
    newManagerName: string;
  }[];
  errorUpdates: {
    index: number;
    errorMsg: string;
    userToReassignId: number;
    userToReassignName: string;
    newManagerId: number;
    newManagerName: string;
  }[];
  createdDateTimeUTC: string;
  withoutSaving: boolean;
}

export interface HierarchicalLogbookParams {
  userId: number;
  search?: string;
  pageNumber?: number;
  pageSize?: number;
  orderColumn?: string;
  descending?: boolean;
  isExport?: boolean;
}

export interface HierarchicalLogEntry {
  id: number;
  systemUserId: number;
  userFullName: string;
  email: string;
  actionType: string;
  fieldUpdates: string;
  changeReason: string;
  areaName: string;
  regionName: string;
  hierarchicalLevelName: string;
  managerFullName: string;
  createdByUserFullName: string;
  createdDateTime: string;
  enabled: boolean;
}

export default {
  instance,
  accounts: {
    /**
     * TRAE TODOS LOS USUARIOS ACTIVOS EN EL SISTEMA CON POSIBLIDAD DE FILTRAR
     *
     * @tags Users
     * @name AllUsers
     * @request GET: /security/api/v1/Accounts/GeneralSearch
     * @secure
     */
    AllUsersByFilters: (
      params: SearchParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<PagedList<UserItem>>>({
        url: `/Accounts/GetUsersByFilters`,
        method: 'GET',
        params,
        paramsSerializer: (prms) =>
          qs.stringify(prms, { arrayFormat: 'repeat' }),
        ...config
      }),
    /**
     * TRAE TODOS LOS USUARIOS ACTIVOS EN EL SISTEMA
     *
     * @tags Users
     * @name GetAllUsers
     * @request GET: /security/api/v1/Accounts
     * @secure
     */
    GetAllUser: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<UserItem[]>>({
        url: '/Accounts',
        method: 'GET',
        ...config
      }),
    /**
    /**
     * TRAE UN USUARIOS POR ID
     *
     * @tags Users
     * @name GetUserById
     * @request GET: /security/api/v1/Accounts/ID
     * @secure
     */
    GetUserById: (id?: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<UserItemByID>>({
        url: `/Accounts/${id}`,
        method: 'GET',
        ...config
      }),

    /**
     * OBTIENE LA ESTRUCTURA ORGANIZACIONAL JERÁRQUICA CON FILTROS ESPECÍFICOS
     *
     * @tags Users
     * @name GetOrganizationalStructure
     * @request GET: /security/api/v1/Accounts/GetOrganizationalStructure
     * @secure
     */
    GetOrganizationalStructure: (
      params: OrganizationalStructureParams = {},
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<OrganizationalStructureResponse>
      >({
        url: `/Accounts/GetOrganizationalStructure`,
        method: 'GET',
        params,
        paramsSerializer: (prms) =>
          qs.stringify(prms, { arrayFormat: 'repeat' }),
        ...config
      }),
    /**
     * OBTIENE LA BITÁCORA DE CAMBIOS DE ESTRUCTURA JERÁRQUICA DE UN USUARIO
     *
     * @tags Users
     * @name GetHierarchicalLogbook
     * @request GET: /security/api/v1/Accounts/GetHierarchicalTrackingLog
     * @secure
     */
    GetHierarchicalLogbook: (
      params: HierarchicalLogbookParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<HierarchicalLogEntry>>
      >({
        url: `/Accounts/GetHierarchicalTrackingLog`,
        method: 'GET',
        params,
        paramsSerializer: (prms) =>
          qs.stringify(prms, { arrayFormat: 'repeat' }),
        ...config
      }),
    /**
     * TRAE TODOS LOS ROLES DEL SISTEMA
     *
     * @tags Roles
     * @name AllRoles
     * @request GET: /security/api/v1/Roles
     * @secure
     */
    AllRoles: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<PagedList<RoleResponse>>>({
        url: `/AccessProfiles`,
        method: 'GET',
        ...config
      }),
    /**
     * TRAE LOS PERMISOS GRANULARES POR ROL
     *
     * @tags Permitions
     * @name PermitionsByRole
     * @request GET: /api/v1/AccessProfiles/1
     * @secure
     */
    PermitionsByRole: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<PermitionsResponse>>({
        url: `/AccessProfiles/${id}`,
        method: 'GET',
        ...config
      }),
    /**
     * BORRA LOS USUARIOS PASANDOLE EL MAIL
     *
     * @tags DeleteUsers
     * @name DeleteUsers
     * @request DELETE: /api/v1/Accounts/DeleteAccount
     * @secure
     */
    DeleteUsers: (idUser: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<any>>({
        url: `/Accounts/${idUser}`,
        method: 'DELETE',
        ...config
      }),
    /**
     * CREA UN NUEVO USUARIO
     *
     * @tags user
     * @name CreateNewuser
     * @request POST: /api/v1/Accounts/CreateAccount
     * @secure
     */
    CreateNewuser: (
      data: CreateNewUseRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<any>>({
        url: `/Accounts/CreateAccount`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * EDITAR USUARIO
     *
     * @tags user
     * @name Edituser
     * @request POST: /api/v1/Accounts/CreateAccount
     * @secure
     */
    EditUser: (data: EditUseRequest, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<any>>({
        url: `/Accounts/UpdateAccount `,
        method: 'PUT',
        data,
        ...config
      }),

    /**
     * OBTENER ALMACENES PARA EL USUARIO
     *
     * @tags user
     * @name GetUserWarehouseList
     * @request GET: /api/v1/UserAssignations/GetUserWarehouseList?UserId={id}
     * @secure
     */
    GetUserWarehouseList: (
      params: {
        userId: number;
        partnerId?: number;
        text?: string;
      },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<WarehouseItem[]>>({
        url: `/UserAssignations/GetUserWarehouseList?UserId=${params.userId}&PartnerId=${params.partnerId}&Text=${params.text}`,
        method: 'GET',
        ...config
      }),

    /**
     * ENVIO DE ALMACENES PARA EL USUARIO
     *
     * @tags user
     * @name AssignUserWarehouse
     * @request POST: /api/v1/UserAssignations/AssignUserWarehouse
     * @secure
     */
    AssignUserWarehouse: (
      data: AssignUserWarehouseRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<any>>({
        url: `/UserAssignations/AssignUserWarehouse`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * OBTENER CLIENTES PARA EL USUARIO
     *
     * @tags user
     * @name GetUserClientsList
     * @request GET: /api/v1/UserAssignations/GetUserClientsList?UserId={id}
     * @secure
     */
    GetUserClientsList: (
      params: { userId: number; partnerId: number; text?: string },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<ClientsAsociatedToUserresponse[]>
      >({
        url: `/UserAssignations/GetUserClientsList?UserId=${params.userId}&PartnerId=${params.partnerId}&Text=${params.text}`,
        method: 'GET',
        ...config
      }),

    /**
     * GetUsersDispatcherByTicket
     * @tags user
     * @name GetUsersDispatcherByTicket
     *  @request GET: /api/v1/Accounts/GetUsersDispatcherByTicket
     * @secure
     */
    GetUsersDispatcherByTicket: (
      ticketId: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<UserItem[]>>({
        url: `/Accounts/GetUsersDispatcherByTicket`,
        method: 'GET',
        params: {
          ticketId
        },
        ...config
      }),

    /**
     * ENVIO DE ALMACENES PARA EL USUARIO
     *
     * @tags user
     * @name AssignUserClients
     * @request POST: /api/v1/UserAssignations/AssignUserClients
     * @secure
     */
    AssignUserClients: (
      data: AssignUserClientsRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<any>>({
        url: `/UserAssignations/AssignUserClients`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * OBTENER FUERZAS DE TRABAJO PARA EL USUARIO
     *
     * @tags user
     * @name GetUserWorkForcesList
     * @request GET: /api/v1/UserAssignations/GetUserWorkForcesList?UserId={id}
     * @secure
     */
    GetUserWorkForcesList: (
      params: { userId: number; partnerId?: number; text?: string },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<WorkForceItem[]>>({
        // eslint-disable-next-line max-len
        url: `/UserAssignations/GetUserWorkForcesList?UserId=${params.userId}&PartnerId=${params.partnerId}&Text=${params.text}`,
        method: 'GET',
        ...config
      }),

    /**
     * ENVIO DE ALMACENES PARA EL USUARIO
     *
     * @tags user
     * @name AssignUserWorkForces
     * @request POST: /api/v1/UserAssignations/AssignUserWorkForces
     * @secure
     */
    AssignUserWorkForces: (
      data: AssignUserWorkForcesRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<any>>({
        url: `/UserAssignations/AssignUserWorkForces`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * OBTENER TIPO DE COMENTARIO PARA EL USUARIO
     *
     * @tags user
     * @name GetUserTicketCommentTypeList
     * @request GET: /api/v1/UserAssignations/GetUserTicketCommentTypeList?UserId={id}
     * @secure
     */
    GetUserTicketCommentTypeList: (
      idUser: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<CommentTypeItem[]>>({
        url: `/UserAssignations/GetUserTicketCommentTypeList?UserId=${idUser}`,
        method: 'GET',
        ...config
      }),

    /**
     * ENVIO DE ALMACENES PARA EL USUARIO
     *
     * @tags user
     * @name AssignUserTicketCommentType
     * @request POST: /api/v1/UserAssignations/AssignUserTicketCommentType
     * @secure
     */
    AssignUserTicketCommentType: (data: any, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<any>>({
        url: `/UserAssignations/AssignUserTicketCommentType`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * OBTENER TIPO DE COMENTARIO PARA EL USUARIO
     *
     * @tags user
     * @name GetUserPermissionsGeneral
     * @request GET: /api/v1/UserAssignations/GetUserPermissionsGeneral?UserId={id}
     * @secure
     */
    GetUserPermissionsGeneral: (
      idUser: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<GeneralPermissionsResponse>>({
        url: `/UserAssignations/GetUserPermissionsGeneral?UserId=${idUser}`,
        method: 'GET',
        ...config
      }),

    /**
     * ENVIO DE PERMISOS GENERALES PARA EL USUARIO
     *
     * @tags user
     * @name AssignUserPermissionsGeneral
     * @request POST: /api/v1/UserAssignations/AssignUserPermissionsGeneral
     * @secure
     */
    AssignUserPermissionsGeneral: (
      data: any,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<any>>({
        url: `/UserAssignations/AssignUserPermissionsGeneral`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * OBTENER TIPOS DE SERVICIO PARA EL USUARIO
     *
     * @tags user
     * @name GetUserServicesList
     * @request GET: /api/v1/UserAssignations/GetUserServicesList?UserId={id}
     * @secure
     */
    GetUserServicesList: (
      params: { userId: number; partnerId?: number },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<UserServiceList>>({
        url: `/UserAssignations/GetUserServicesList`,
        method: 'GET',
        params,
        ...config
      }),

    /**
     * ENVIO DE SERVICIOS PARA EL USUARIO
     *
     * @tags user
     * @name AssignUserServices
     * @request POST: /api/v1/UserAssignations/AssignUserServices
     * @secure
     */
    AssignUserServices: (
      data: SettingsUserPayload,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/UserAssignations/AssignUserServices`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * Genera un UserName por defecto concatenando el primer nombre con
     * las dos primeras letras del apellido mas un número Parameters
     *
     * @tags account
     * @name GenerateDefaultUserName
     * @request POST: /api/v1/Accounts/GenerateDefaultUserName
     * @secure
     */
    GenerateDefaultUserName: (
      data: GenerateDefaultUserName,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<{ defeaultUserName: string }>
      >({
        url: `/Accounts/GenerateDefaultUserName`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * OBTENER DETALLES DEL USUARIO
     * @tags user
     * @name GetAccessList
     * @request GET: /api/v1/Accounts/me/GetAccessList
     */
    GetDetailsUser(config: AxiosRequestConfig = {}) {
      return instance.request<void, AxiosServiceResponse<GetUserDetail>>({
        url: `/Accounts/me`,
        method: 'GET',
        ...config
      });
    },

    /**
     * OBTENER PERMISOS PARA EL USUARIO
     * @tags user
     * @name GetAccessList
     * @request GET: /api/v1/Accounts/me/GetAccessList
     */
    GetAccessList(config: AxiosRequestConfig = {}) {
      return instance.request<void, AxiosServiceResponse<AccessList[]>>({
        url: `/Accounts/me/GetAccessList`,
        method: 'GET',
        ...config
      });
    },

    /**
     * ACTUALIZA LOS PERMISOS PARA UN USUARIO EN ESPECIFICO
     *
     * @tags user
     * @name updatePermitions
     * @request POST: /api/v1/UserAssignations/AssignUserAccessElements
     * @secure
     */
    updatePermitions: (
      data: UpdateProfileRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<any>>({
        url: `/UserAssignations/AssignUserAccessElements`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * TRAE LOS PERMISOS GRANULARES DE UN USUARIO
     *
     * @tags user
     * @name GetUserPermission
     * @request GET: /UserAssignations/GetUserUserAccessElementList
     * @secure
     */
    GetUserPermission: (userID: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<AccessElementsUser[]>>({
        url: `/UserAssignations/GetUserUserAccessElementList?UserId=${userID}`,
        method: 'GET',
        ...config
      }),

    /**
     * Clona la lista de "permisos" un usuario origen a uno o varios usuarios destino
     * siempre y cuando todos tengan el mismo perfil
     *
     * @tags user
     * @name CloneUserAccessElement
     * @request POST: /api/v1/UserAssignations/CloneUserAccessElement
     * @secure
     */
    CloneUserAccessElement: (
      data: CloneUserAccessElement,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<any>>({
        url: `/UserAssignations/CloneUserAccessElement`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * Obtiene todos los tipos de asignaciones en falso por defecto
     * @tags user
     * @name GetAssignationList
     * @request GET: /api/v1/UserAssignations/GetAssignationList
     * @secure
     */
    GetAssignationList: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<Assignation[]>>({
        url: `/UserAssignations/GetAssignationList`,
        method: 'GET',
        ...config
      }),

    /**
     * Clona la lista de "asignaciones" desde un usuario origen a uno o varios usuarios destino,
     * siempre y cuando todos tengan el mismo perfil
     * @tags user
     * @name CloneUserAssignations
     * @request POST: /api/v1/UserAssignations/CloneUserAssignations
     * @secure
     */
    CloneUserAssignations: (
      data: CloneUserAssignations,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<any>>({
        url: `/UserAssignations/CloneUserAssignations`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * Me retrona datos con los cuales sabemos si se puede inactivar o borrar un jefe de almacen
     *
     * @tags user
     * @name CanWarehouseAdminBeInactivate
     * @request GET: /api/v1/Accounts/CanWarehouseAdminBeInactivate
     * @secure
     */
    CanWarehouseAdminBeInactivate: (
      id: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<CanWarehouseAdminBeInactivate>
      >({
        url: `/Accounts/CanWarehouseAdminBeInactivate?warehouseAdminId=${id}`,
        method: 'GET',
        ...config
      }),
    /**
     * EDITA UN MAIL DE UN PERFIL
     *
     * @tags Security-Account
     * @name EditAccountEmail
     * @request PUT: /api/v1/Accounts/UpdateAccountEmail
     * @secure
     */
    EditAccountEmail: (
      data: UpdateAccountEmail,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<any>>({
        url: `/Accounts/UpdateAccountEmail`,
        method: 'PUT',
        data,
        ...config
      }),

    /**
     * Actualiza el idioma del usuario
     * @tags user
     * @name UpdateAccountLanguage
     * @request PUT: /api/v1/Accounts/UpdateAccountLanguage
     * @secure
     */
    UpdateAccountLanguage: (
      { id, langId }: { id: number; langId: number },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<any>>({
        url: `/Accounts/UpdateAccountLanguage`,
        method: 'PUT',
        data: { id, langId },
        ...config
      }),

    /**
     * Obtiene los candidatos para ser jefe de almacen
     * @tags user
     * @name GetManagerCandidates
     * @request GET: /api/v1/Accounts/GetManagerCandidates
     * @secure
     */
    GetManagerCandidates: (
      params: GetManagerCandidatesParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<ManagerCandidateResponse[]>>({
        url: `/Accounts/GetManagerCandidates`,
        method: 'GET',
        params,
        paramsSerializer: (prms) =>
          qs.stringify(prms, { arrayFormat: 'repeat' }),
        ...config
      }),

    /** Consultar cuantos tickets abiertos tiene un ingeniero asignados
     * @tags Accounts
     * @name GetOpenTicketsCount
     * @request GET: /api/v1/Accounts/{id}/OpenTicketsCount
     * @secure
     */
    GetOpenTicketsCount: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<OpenTicketsCountResponse>>({
        url: `/Accounts/${id}/OpenTicketsCount`,
        method: 'GET',
        ...config
      }),

    /**
     * Obtiene las personas a cargo de un usuario específico
     * @tags Accounts
     * @name GetPeopleInCharge
     * @request GET: /api/v1/Accounts/GetPeopleInCharge/{id}
     * @secure
     */
    GetPeopleInCharge: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<PeopleInChargeItem[]>>({
        url: `/Accounts/GetPeopleInCharge/${id}`,
        method: 'GET',
        ...config
      }),

    /**
     * Carga masiva de estructura jerárquica
     * @tags HierarchicalStructure
     * @name BulkLoadHierarchicalStructure
     * @request POST: /api/v1/Accounts/BulkUpdateHierarchy
     * @secure
     */
    BulkLoadHierarchicalStructure: (
      data: HierarchicalMassiveLoad,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<BulkLoadHierarchicalStructureResponse>
      >({
        url: `/Accounts/BulkUpdateHierarchy`,
        method: 'POST',
        data,
        ...config
      })
  },
  roles: {
    /**
     * Obtiene todos los usuarios Jefe de Almacen Activos
     * @tags roles
     * @name getWarehouseAdminUsers
     * @request GET: /api/v1/Roles/WarehouseAdminUsers
     * @secure
     */
    getWarehouseAdminUsers: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<WarehouseAdminUser[]>>({
        url: `/Roles/WarehouseAdminUsers`,
        method: 'GET',
        ...config
      }),
    /**
     * Obtiene todos los usuarios por id de usuario
     * @tags roles
     * @name getUsersWithSameRolesByUserId
     * @request GET: /api/v1/Roles/GetUsersWithSameRolesByUserId/id
     * @secure
     */
    getUsersWithSameRolesByUserId: (
      id: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<UserItem[]>>({
        url: `/Roles/GetUsersWithSameRolesByUserId/${id}`,
        method: 'GET',
        ...config
      })
  },
  profiles: {
    /**
     * TRAE TODOS LOS PERFILES EXISTENTES
     *
     * @tags Security-Profiles
     * @name GetAllProfiles
     * @request GET: api/v1/AccessProfiles
     * @secure
     */
    GetAllProfiles: (params?: SearchParams, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<PagedList<RoleItem>>>({
        url: `/AccessProfiles`,
        method: 'GET',
        params,
        ...config
      }),
    /**
     * TRAE TODOS LOS PERFILES EXISTENTES ACTIVOS
     *
     * @tags Security-Profiles
     * @name GetAllProfiles
     * @request GET: api/v1/AccessProfiles/GetAllActives
     * @secure
     */
    GetAllActiveProfiles: (
      params?: SearchParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<RoleItem[]>>({
        url: `/AccessProfiles/GetAllActives`,
        method: 'GET',
        params,
        ...config
      }),
    /**
     * CREA UN NUEVO PERFIL
     *
     * @tags Security-Profiles
     * @name CreateNewProfile
     * @request POST: /api/v1/AccessProfiles
     * @secure
     */
    CreateNewProfile: (data: ProfileRequest, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<any>>({
        url: `/AccessProfiles`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * BORRA UN PERFIL
     *
     * @tags Security-Profiles
     * @name DeleteProfile
     * @request DELETE: /api/v1/AccessProfiles/{id}
     * @secure
     */
    DeleteProfile: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<any>>({
        url: `/AccessProfiles/${id}`,
        method: 'DELETE',
        ...config
      }),
    /**
     * EDITA UN PERFIL
     *
     * @tags Security-Profiles
     * @name EditProfile
     * @request PUT: /api/v1/AccessProfiles/{id}
     * @secure
     */
    EditProfile: (
      data: ProfileEditionRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<any>>({
        url: `/AccessProfiles`,
        method: 'PUT',
        data,
        ...config
      }),
    /**
     * TRAE TODOS LOS PERFILES
     *
     * @tags Roles
     * @name AllRoles
     * @request GET: /api/v1/AccessProfiles/GetAllActives
     * @secure
     */
    GetAllActives: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<RoleResponse[]>>({
        url: `/AccessProfiles/GetAllActives`,
        method: 'GET',
        ...config
      })
  },
  permissions: {
    /**
     * TRAE TODOS LOS PERMISOS
     *
     * @tags Security-Permissions
     * @name GetAllPermissions
     * @request GET: /api/v1/AccessElements/GetAllWithModuleTree
     * @secure
     */
    GetAllPermissions: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<AccessElements[]>>({
        url: `/AccessElements/GetAllWithModuleTree`,
        method: 'GET',
        ...config
      }),
    /**
     * TRAE TODOS LOS PERMISOS DE OPERACION DE UN USUARIO
     *
     * @tags Security-Permissions
     * @name GetAllOperatingPermission
     * @request GET: /api/v1/UserAssignations/GetUserWorkFlowType
     * @secure
     */
    GetAllOperatingPermission: (
      params: { userId: number; partnerId?: number },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<OperatingPermission>>({
        url: `/UserAssignations/GetUserWorkFlow`,
        method: 'GET',
        params,
        ...config
      }),
    /**
     * GUARDADO DE LOS PERMISOS DE OPERACION DE UN USUARIO
     *
     * @tags Security-Permissions
     * @name SaveOperatingPermission
     * @request POST: /api/v1/UserAssignations/AssignUserWorkFlow
     * @secure
     */
    SaveOperatingPermission: (
      data: SubmitOperatingPermission,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/UserAssignations/AssignUserWorkFlow`,
        method: 'POST',
        data,
        ...config
      })
  },
  loging: {
    /**
     * TRAE TODOS LOS ACCESOS AL SISTEMA FILTRADOS POR FECHA
     *
     * @tags Security-Loging
     * @name GetAccessLogbookResponse
     * @request GET: /api/v1/Login/GetLoginTrackingByFilters
     * @secure
     */
    GetAccessLogbookByFilter: (
      params?: AccessLogbookParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<AccessLogbookResponse>>
      >({
        url: `/Login/GetLoginTrackingByFilters`,
        method: 'GET',
        params,
        ...config
      }),
    /**
     * TRAE TODOS LOS TOKENS
     *
     * @tags Security-Loging
     * @name GetTokenTypes
     * @request GET: /api/v1/Login/GetTokenTypes
     * @secure
     */
    GetTokenTypes: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<TokenType[]>>({
        url: `/Login/GetTokenTypes`,
        method: 'GET',
        ...config
      }),
    /**
     * RESET DE CONTRASEÑA
     *
     * @tags Security-Loging
     * @name ResetPasswordAccount
     * @request POST: /api/v1/Accounts/ResetPasswordAccount
     * @secure
     */
    ResetPasswordAccount: (
      data: { email: string },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Accounts/ResetPasswordAccount`,
        method: 'POST',
        data,
        ...config
      }),
    /**
     * ADD LOGIN TRACKING
     *
     * @tags Security-Loging
     * @name AddLoginTracking
     * @request POST: /api/v1/Login/AddLoginTracking
     * @secure
     */
    AddLoginTracking: (
      data: AddLogingTrackingRequest,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<unknown>>({
        url: `/Login/AddLoginTracking`,
        method: 'POST',
        data,
        ...config
      })
  },
  assignations: {
    /**
     * TRAE TODOS CLIENTES ASOCIADOS AL USUARIO
     *
     * @tags Security-ClientsByUserID
     * @name GetClientsByUser
     * @request GET: /api/v1/UserAssignations/GetClientsByUser?UserId=userID
     * @secure
     */
    GetClientsByUser: (userID: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<DataClientsByUser>>({
        url: `/UserAssignations/GetClientsByUser?UserId=${userID}`,
        method: 'GET',
        ...config
      }),
    /**
     * TRAE TODAS LAS FUERZAS DE TRABAJO ASOCIADOS AL USUARIO
     *
     * @tags Security-GetWorkForcesByUser
     * @name GetWorkForcesByUser
     * @request GET: /api/v1/UserAssignations/GetWorkForcesByUser?UserId=userID
     * @secure
     */
    GetWorkForcesByUser: (
      params: WorkForceRelatedToUserIdParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<WorkForceRelatedToUserIdResponse>>
      >({
        url: `/UserAssignations/GetWorkForcesByUser`,
        method: 'GET',
        params,
        ...config
      }),
    /**
     * TRAE TODAS LAS FUERZAS DE TRABAJO ASOCIADOS AL CLIENTE
     *
     * @tags Security-GetWorkForcesChangeByClientId
     * @name GetWorkForcesChangeByClientId
     * @request GET: /api/v1/UserAssignations/GetUsersByClient?ClientId=105
     * @secure
     */
    GetWorkForcesChangeByClientId: (
      params: WorkForceRelatedToClientIdParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<WorkForceRelatedToClientIdResponse[]>
      >({
        url: `/UserAssignations/GetUsersByClient`,
        method: 'GET',
        params,
        ...config
      }),
    /**
     * TRAE TODAS LAS FUERZAS DE TRABAJO ASOCIADOS AL CLIENTE
     *
     * @tags Security-GetWorkForcesByClientId
     * @name GetWorkForcesByClientId
     * @request GET: /api/v1/UserAssignations/GetUsersByClient?ClientId=105
     * @secure
     */
    GetWorkForcesByClientId: (
      params: WorkForceRelatedToClientIdParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<WorkForceRelatedToClientIdResponse[]>
      >({
        url: `/UserAssignations/GetUsersByClient`,
        method: 'GET',
        params,
        ...config
      }),
    /**
     * TRAE TODAS LAS FUERZAS DE TRABAJO ASOCIADOS AL CLIENTE
     *
     * @tags Security-GetWorkForcesByClientId
     * @name GetWorkForcesGroupByClientId
     * @request GET: /api/v1/UserAssignations/GetUsersByClient?ClientId=105
     * @secure
     */
    GetWorkForcesGroupByClientId: (
      params: WorkForceRelatedToClientIdParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<WorkForceRelatedToClientIdResponse[]>
      >({
        url: `/UserAssignations/GetUsersByClient`,
        method: 'GET',
        params,
        ...config
      }),
    /**
     * Obtiene el tracking de asignación de permisos de usuario
     *
     * @tags user
     * @name GetAccessElementTracking
     * @request GET: /api/v1/UserAssignations/GetAccessElementTracking
     * @secure
     */
    GetAccessElementTracking: (
      params: IAccessElementTrackingParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<PagedList<IAccessElementTracking>>
      >({
        url: `/UserAssignations/GetAccessElementTracking`,
        method: 'GET',
        params,
        ...config
      })
  },
  public: {
    /**
     * Obtiene los parámetros generales del sistema
     * @tags Security-GetParams
     * @name GetParams
     * @request GET: /api/v1/Public/GetParams
     * @secure
     */
    GetParams: (
      params: { platformSpec: 'app' | 'WEB' },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosResponse<GetParamsResponse>>({
        url: `/Public/GetParams`,
        method: 'GET',
        params,
        ...config
      })
  },
  systemGroups: {
    /**
     * Obtiene los parámetros generales del sistema
     * @tags SystemGroups
     * @name getAll
     * @request GET: /api/v1/SystemGroups/GetAll
     * @secure
     */
    getAll: (config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<SystemGroup[]>>({
        url: `/SystemGroups/GetAll`,
        method: 'GET',
        ...config
      }),

    /**
     * Crea un nuevo grupo
     * @tags SystemGroups
     * @name create
     * @request POST: /api/v1/SystemGroups
     * @secure
     */
    create: (data: SystemGroup, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<SystemGroup>>({
        url: `/SystemGroups`,
        method: 'POST',
        data,
        ...config
      }),

    /**
     * Actualiza un grupo
     * @tags SystemGroups
     * @name update
     * @request PUT: /api/v1/SystemGroups
     * @secure
     */
    update: (data: SystemGroup, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<SystemGroup>>({
        url: `/SystemGroups`,
        method: 'PUT',
        data,
        ...config
      }),

    /**
     * Elimina un grupo
     * @tags SystemGroups
     * @name delete
     * @request DELETE: /api/v1/SystemGroups/{id}
     * @secure
     */
    delete: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<any>>({
        url: `/SystemGroups/${id}`,
        method: 'DELETE',
        ...config
      }),

    /**
     * Obtiene un grupo
     * @tags SystemGroups
     * @name get
     * @request GET: /api/v1/SystemGroups/{id}
     * @secure
     */
    get: (id: number, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<SystemGroup>>({
        url: `/SystemGroups/${id}?includeUsers=true&activeUsersOnly=true`,
        method: 'GET',
        ...config
      }),

    /**
     * Obtiene todos los accesos marcados en verdadero si y solo si el grupo tiene acceso a ellos, organizado por modulos
     * @tags SystemGroups
     * @name getSystemGroupsAccessElements
     * @request GET: /api/v1/SystemGroups/SystemGroupAccessElementList
     * @secure
     */
    getSystemGroupsAccessElements: (
      systemGroupId: number,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<AccessElements[]>>({
        url: `/SystemGroups/SystemGroupAccessElementList`,
        method: 'GET',
        params: {
          systemGroupId
        },
        ...config
      }),

    /**
     * Obtiene todos los clientes marcados en verdadero si y solo si el grupo tiene acceso a ellos
     * @tags SystemGroups
     * @name getSystemGroupClientList
     * @request GET: /api/v1/SystemGroups/GetSystemGroupClientList
     * @secure
     */
    getSystemGroupClientList: (
      params?: ISystemGroupClientListParams,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<
        void,
        AxiosServiceResponse<ClientsAsociatedToUserresponse[]>
      >({
        url: `/SystemGroups/GetSystemGroupClientList`,
        method: 'GET',
        params,
        ...config
      }),
    /**
     * Búsqueda general paginada por grupos
     * @tags SystemGroups
     * @name generalSearch
     * @request GET: /api/v1/SystemGroups/GeneralSearch
     * @secure
     */
    generalSearch: (params: GeneralSearch, config: AxiosRequestConfig = {}) =>
      instance.request<void, AxiosServiceResponse<PagedList<SystemGroup>>>({
        url: `/SystemGroups/GeneralSearch`,
        method: 'GET',
        params,
        ...config
      }),
    /**
     * GetAll de grupos por clientId y usuarios que con permisos sobre el clientid
     * @tags SystemGroups
     * @name getSystemGroupByClientIdQuery
     * @request GET: /api/v1/SystemGroups/GetSystemGroupByClientIdQuery
     * @secure
     */
    getSystemGroupByClientIdQuery: (
      params: {
        clientId?: number | string;
        engineerOnly?: boolean;
        enabled?: boolean;
      },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<SystemGroup[]>>({
        url: `/SystemGroups/GetSystemGroupByClientIdQuery`,
        method: 'GET',
        params,
        ...config
      }),

    /**
     * Altera lista de permisos que tiene acceso un grupo
     * @tags SystemGroups
     * @name assignSystemGroupAccessElements
     * @request POST: /api/v1/SystemGroups/AssignSystemGroupAccessElements
     *  @secure
     */
    assignSystemGroupAccessElements: (
      data: AssignSystemGroupAccessElements,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<any>>({
        url: `/SystemGroups/AssignSystemGroupAccessElements`,
        method: 'POST',
        data,
        ...config
      }),

    /** Búsqueda de usuarios por grupo
     * @tags SystemGroups
     * getUsersBySystemGroup
     * @request GET: /api/v1/SystemGroups/{id}/GetUsers
     * @secure
     */
    getUsersBySystemGroup: (
      params: SystemGroupUsersSearch,
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosServiceResponse<PagedList<UserItem>>>({
        url: `/SystemGroups/${params.id}/Users`,
        method: 'GET',
        params,
        ...config
      })
  },
  generalParams: {
    /** Trae parametros generales del sistema
     * @tags SystemGroups
     * getGeneralParams
     * @request GET: /api/v1/Public/GetParams?platformSpec=web
     * @secure
     */
    getGeneralParams: (
      params: {
        platformSpec: string;
      },
      config: AxiosRequestConfig = {}
    ) =>
      instance.request<void, AxiosResponse<GeneralParams>>({
        url: `/Public/GetParams`,
        method: 'GET',
        params,
        ...config
      })
  }
};
